import { API } from "./APIClient";
import { setAuthSession } from "../utils/authStorage";

class AuthAPIClass {
    _saveSession(data) {
        if (!data?.access_token || !data?.refresh_token) {
            return;
        }

        setAuthSession({
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
        });
    }

    _mapTokenResponse(data = {}) {
        return {
            accessToken: data.access_token || null,
            refreshToken: data.refresh_token || null,
            tokenType: data.token_type || "bearer",
            expiresIn: data.expires_in || null,
            userId: data.user_id || null,
            requires2fa: Boolean(data.requires_2fa),
            challengeId: data.challenge_id || null,
            message: data.message || null,
        };
    }

    _mapChallengeResponse(data = {}) {
        return {
            requires2fa: Boolean(data.requires_2fa),
            challengeId: data.challenge_id || null,
            expiresIn: data.expires_in || null,
            message: data.message || null,
            twoFaEnabled: typeof data.two_fa_enabled === "boolean" ? data.two_fa_enabled : null,
        };
    }

    async login(email, password) {
        const response = await API.post("/auth/login", {
            email,
            password,
        });

        this._saveSession(response.data);
        return this._mapTokenResponse(response.data);
    }

    async register(name, email, password, inviteToken = null) {
        const payload = {
            name,
            email,
            password,
        };

        if (inviteToken) {
            payload.invite_token = inviteToken;
        }

        const response = await API.post("/auth/register", payload);

        this._saveSession(response.data);

        return this._mapTokenResponse(response.data);
    }

    async refresh(refreshToken) {
        const response = await API.post("/auth/refresh", {
            refresh_token: refreshToken,
        });

        this._saveSession(response.data);
        return this._mapTokenResponse(response.data);
    }

    async verifyLoginOtp(challengeId, code) {
        const response = await API.post("/auth/login/verify-otp", {
            challenge_id: challengeId,
            code,
        });

        this._saveSession(response.data);
        return this._mapTokenResponse(response.data);
    }

    async resendLoginOtp(challengeId) {
        const response = await API.post("/auth/login/resend-otp", {
            challenge_id: challengeId,
        });

        return this._mapChallengeResponse(response.data);
    }

    async requestEnable2fa() {
        const response = await API.post("/auth/2fa/enable/request");
        return this._mapChallengeResponse(response.data);
    }

    async get2faStatus() {
        const response = await API.get("/auth/2fa/status");
        return {
            is2faEnabled: Boolean(response.data?.is_2fa_enabled),
        };
    }

    async verifyEnable2fa(challengeId, code) {
        const response = await API.post("/auth/2fa/enable/verify", {
            challenge_id: challengeId,
            code,
        });

        return this._mapChallengeResponse(response.data);
    }

    async requestDisable2fa() {
        const response = await API.post("/auth/2fa/disable/request");
        return this._mapChallengeResponse(response.data);
    }

    async verifyDisable2fa(challengeId, code) {
        const response = await API.post("/auth/2fa/disable/verify", {
            challenge_id: challengeId,
            code,
        });

        return this._mapChallengeResponse(response.data);
    }

    async validateInvite(inviteToken) {
        const response = await API.post("/invite/validate", {
            invite_token: inviteToken,
        });

        return response.data;
    }

    async acceptInvite(inviteToken) {
        const response = await API.post("/invite/accept", {
            invite_token: inviteToken,
        });

        return response.data;
    }
}

const AuthAPI = new AuthAPIClass();
export default AuthAPI;
